import type { StateCreator } from 'zustand';
import type { VoyagerState } from '../useVoyagerStore';
import type { ConversationThread } from '../../types';
import { generateId } from '../../utils';
import { mintOpId } from '../../components/Agents/opId';
import { getPersistenceNamespace, parkAgentChatSnapshot } from '../../stores/helpers/persistenceHelpers';
import {
  listGroups as apiListGroups, createGroup as apiCreateGroup, disbandGroup as apiDisbandGroup, groupChat as apiGroupChat,
  type GroupRow, type GroupCreate, type GroupChatTurn,
  type ActivityRow,
  type AgentChatTurn,
  type AgentRow,
  type AgentRun,
  type AgentRunSummary,
  type DeployRequest,
  type EmailDelegateBody,
  type EmailDigest,
  type EmailView,
  type Guardrail,
  type MemoryRow,
  type Seat,
  type Wall,
  agentErrorMessage,
  chatWithAgent as apiChat,
  deployAgent as apiDeploy,
  getAgentChatOpening as apiOpening,
  getAgentEmail as apiEmail,
  getAgentWall as apiWall,
  verifyAgentNow as apiVerifyNow,
  listAgentActivity as apiActivity,
  listAgentMemory as apiMemory,
  listAgentRuns as apiRuns,
  listAgents as apiList,
  listSeats as apiSeats,
  pauseAgent as apiPause,
  readAgentEmailNow as apiReadEmail,
  resumeAgent as apiResume,
  retireAgent as apiRetire,
  runAgent as apiRun,
  setAgentGuardrails as apiSetGuardrails,
  setEmailDelegate as apiSetEmailDelegate,
  steerAgent as apiSteer,
  subscribeAgent as apiSubscribe,
  unsubscribeAgent as apiUnsubscribe,
} from '../../components/Agents/api';

export interface AgentDetail {
  runs: AgentRunSummary[];
  memory: MemoryRow[];
  activity: ActivityRow[];
}

export interface AgentsSlice {
  agents: AgentRow[];
  agentsLoading: boolean;
  agentsError: string | null;
  seats: Seat[];
  agentDetail: Record<string, AgentDetail>;
  /** Each agent's wall (#202), by id, as last fetched. */
  agentWalls: Record<string, Wall>;
  agentWallError: string | null;
  /** Each agent's email grant + weekly digests (#205), by id, as last fetched. */
  agentEmail: Record<string, EmailView>;
  /** Bumped on every identity/workspace switch; a fetch that outlived one is dropped. */
  /** Groups (#307): a named set of seats with an address that routes. */
  groups: GroupRow[];
  groupsError: string | null;
  fetchGroups: () => Promise<void>;
  createGroup: (body: GroupCreate) => Promise<GroupRow>;
  disbandGroup: (id: string) => Promise<void>;
  /** Open (or return to) the GROUP tab: a durable thread of kind `group` whose turns are the group's replies. */
  openGroupChat: (groupId: string, groupName: string) => string;
  /** One line to the group: the member whose objective binds answers in its own name, else the roll-up. */
  deliverGroupTurn: (threadId: string, groupId: string, turn: GroupChatTurn) => Promise<void>;
  agentsGeneration: number;
  agentsLoadedGeneration: number | null;

  /** The workspace changed: forget every agent, wall and detail — they belong to the one we left. */
  resetAgentsWorkspace: () => void;
  fetchAgents: (options?: { ifNeeded?: boolean }) => Promise<void>;
  fetchSeats: () => Promise<void>;
  deployAgent: (payload: DeployRequest) => Promise<AgentRow>;
  pauseAgent: (id: string) => Promise<AgentRow>;
  resumeAgent: (id: string) => Promise<AgentRow>;
  /** Runs the loop once, now; the row's `last_run` becomes this run. */
  runAgentNow: (id: string) => Promise<AgentRun>;
  retireAgent: (id: string) => Promise<AgentRow>;
  /** The holder edits the objective's guardrails (#571); a wall on screen is re-read so its ledger shows the change. */
  setAgentGuardrails: (id: string, guardrails: Guardrail[]) => Promise<AgentRow>;
  /** The holder opens or closes the scoped email grant (#205); the wall and the digests are re-read so the grant row follows. */
  setEmailDelegate: (id: string, body: EmailDelegateBody) => Promise<AgentRow>;
  /** Load the agent's email grant + weekly digests (#205) into `agentEmail[id]`. */
  fetchAgentEmail: (id: string) => Promise<void>;
  /** Read the scoped mailbox now (#205): the new digest leads `agentEmail[id]`, and the wall is re-read for the fresh ledger row. */
  readAgentEmailNow: (id: string) => Promise<EmailDigest>;
  /** Ask the Verifier (#203) to score now: the wall's verification + the agent's badge follow the result. */
  verifyAgentNow: (id: string) => Promise<void>;
  fetchAgentDetail: (id: string) => Promise<void>;
  fetchAgentWall: (id: string) => Promise<void>;
  /** Follow the agent (or stop): the wall's `subscribed` / `subscribers` follow the server's answer. */
  subscribeAgent: (id: string, on: boolean) => Promise<void>;
  /** Steer it in one line: the signed row leads the wall's steering and its ledger. */
  steerAgent: (id: string, text: string) => Promise<void>;
  /**
   * Chat with the agent (#202): the open `agent_chat` thread for it, else a
   * new one — a durable thread with `source_kind: agent`, its transcript
   * client-owned in `snapshot.turns`. Returns the thread id.
   */
  openAgentChat: (agentId: string, agentName: string) => string;
  /**
   * The agent's opening line for an empty chat (#202), written to the
   * thread's snapshot — store-owned, so it lands whether or not the tab is
   * still on screen. One request per thread at a time; a chat that already
   * has turns is left alone. Rejects with the server's error.
   */
  openChatTurn: (threadId: string, agentId: string) => Promise<void>;
  /**
   * Deliver one user turn (#202): it goes into the snapshot at once, the
   * agent is asked with the turn's own `op_id` (a retry reuses it), and the
   * reply is appended — or the turn marked failed — in the store, so a reply
   * that lands while another tab is open is still recorded. Rejects with
   * the server's error so the screen can say so.
   */
  deliverChatTurn: (threadId: string, agentId: string, turn: AgentChatTurn) => Promise<void>;
}

const replaceAgent = (list: AgentRow[], row: AgentRow) =>
  list.some((a) => a.id === row.id) ? list.map((a) => (a.id === row.id ? { ...a, ...row } : a)) : [...list, row];

export const createAgentsSlice: StateCreator<
  VoyagerState, [], [], AgentsSlice
> = (set, get) => {
  /**
   * Every request captures the workspace generation before it goes out and
   * applies its result only if no identity/org switch happened meanwhile —
   * a row from the workspace we left must never land in this one. The
   * result is still returned, so a caller's own flow (a dialog closing, a
   * card's busy state) is unaffected.
   */
  const guarded = async <T,>(work: () => Promise<T>, apply: (result: T) => void): Promise<T> => {
    const gen = get().agentsGeneration;
    const result = await work();
    if (get().agentsGeneration === gen) apply(result);
    return result;
  };
  const guardedRow = (work: () => Promise<AgentRow>) =>
    guarded(work, (row) => set((s) => ({ agents: replaceAgent(s.agents, row) })));

  /** The chat's turns as the store holds them now. */
  const chatTurns = (threadId: string): AgentChatTurn[] =>
    ((get().savedThreads.find((t) => t.id === threadId)?.landingAgentTurns || []) as AgentChatTurn[]);
  /** Openings in flight, by thread: a second mount joins the first request. */
  const openings = new Map<string, Promise<void>>();
  const creations = new Map<string, Promise<boolean>>();
  /** A wall on screen follows a change made through another route (a grant, an invitation, an ejection). */
  const refreshWall = (id: string) => { if (get().agentWalls[id]) get().fetchAgentWall(id).catch(() => {}); };

  return {
  agents: [],
  agentsLoading: false,
  agentsError: null,
  seats: [],
  agentDetail: {},
  agentWalls: {},
  agentWallError: null,
  agentEmail: {},
  groups: [], groupsError: null,
  agentsGeneration: 0,
  agentsLoadedGeneration: null,

  resetAgentsWorkspace: () => {
    const onAgents = get().currentView === 'agents' || get().currentView === 'agent_wall' || get().currentView === 'objectives'
      || (get().currentView === 'agent_studio' && get().studioTab === 'agents');
    set({
      agents: [], agentsLoading: false, agentsError: null, seats: [], agentDetail: {}, agentWalls: {}, agentWallId: null, agentWallError: null, agentEmail: {}, groups: [], groupsError: null,
      agentsGeneration: get().agentsGeneration + 1,
      agentsLoadedGeneration: null,
      // A wall from the previous workspace has nothing to show here: back to the index (Studio → Agents, #198).
      ...(get().currentView === 'agent_wall' ? { currentView: 'agent_studio' as const, studioTab: 'agents' as const } : {}),
      // an objective from the previous workspace has nothing to show here either: back to the list (#571)
      ...(get().currentView === 'objectives' ? { objectiveId: null } : {}),
    });
    if (onAgents) get().fetchAgents().catch(() => {});
  },

  fetchAgents: async (options) => {
    const gen = get().agentsGeneration;
    if (options?.ifNeeded && (get().agentsLoadedGeneration === gen || get().agentsLoading)) return;
    set({ agentsLoading: true, agentsError: null });
    try {
      const list = await apiList();
      if (get().agentsGeneration !== gen) return;
      set({ agents: Array.isArray(list) ? list : [], agentsLoading: false, agentsLoadedGeneration: gen });
    } catch (e: any) {
      if (get().agentsGeneration !== gen) return;
      set({ agentsLoading: false, agentsError: agentErrorMessage(e, 'Could not load agents') });
    }
  },

  fetchSeats: async () => {
    await guarded(() => apiSeats().catch(() => [] as Seat[]), (list) => set({ seats: Array.isArray(list) ? list : [] }));
  },

  deployAgent: async (payload) => {
    const row = await guardedRow(() => apiDeploy(payload));
    get().fetchSeats().catch(() => {});
    return row;
  },

  pauseAgent: (id) => guardedRow(() => apiPause(id)),

  resumeAgent: (id) => guardedRow(() => apiResume(id)),

  runAgentNow: async (id) => {
    const run = await guarded(() => apiRun(id), (result) => {
      const { why: _why, summary: _summary, context: _context, ...summary } = result;
      set((s) => ({
        agents: s.agents.map((a) => (a.id === id
          ? { ...a, last_run: summary, last_run_id: result.id, last_run_status: result.status, last_run_at: result.finished_at || result.started_at }
          : a)),
      }));
      get().fetchAgentDetail(id).catch(() => {});
    });
    return run;
  },

  retireAgent: (id) => guardedRow(() => apiRetire(id)),

  setAgentGuardrails: async (id, guardrails) => {
    const row = await guardedRow(() => apiSetGuardrails(id, guardrails));
    refreshWall(id);
    return row;
  },

  setEmailDelegate: async (id, body) => {
    const row = await guardedRow(() => apiSetEmailDelegate(id, body));
    refreshWall(id);
    if (get().agentEmail[id]) get().fetchAgentEmail(id).catch(() => {});
    return row;
  },

  fetchAgentEmail: async (id) => {
    await guarded(
      () => apiEmail(id).catch(() => null),
      (view) => { if (view) set((s) => ({ agentEmail: { ...s.agentEmail, [id]: view } })); },
    );
  },

  readAgentEmailNow: async (id) => {
    const digest = await guarded(() => apiReadEmail(id), (d) => set((s) => {
      const view = s.agentEmail[id];
      if (!view) return {};
      return { agentEmail: { ...s.agentEmail, [id]: { ...view, digests: [d, ...view.digests.filter((x) => x.id !== d.id)] } } };
    }));
    refreshWall(id);
    return digest;
  },

  verifyAgentNow: async (id) => {
    await guarded(() => apiVerifyNow(id), (res) => set((s) => {
      const wall = s.agentWalls[id];
      if (!wall) return {};
      return { agentWalls: { ...s.agentWalls, [id]: { ...wall, verification: res.verification } } };
    }));
    refreshWall(id);
  },

  fetchAgentDetail: async (id) => {
    await guarded(
      () => Promise.all([
        apiRuns(id).catch(() => [] as AgentRunSummary[]),
        apiMemory(id).catch(() => [] as MemoryRow[]),
        apiActivity(id).catch(() => [] as ActivityRow[]),
      ]),
      ([runs, memory, activity]) => set((s) => ({ agentDetail: { ...s.agentDetail, [id]: { runs, memory, activity } } })),
    );
  },

  fetchAgentWall: async (id) => {
    const gen = get().agentsGeneration;
    set({ agentWallError: null });
    try {
      const wall = await apiWall(id);
      // Started under a workspace we have since left: drop it.
      if (get().agentsGeneration !== gen) return;
      set((s) => ({ agentWalls: { ...s.agentWalls, [id]: wall } }));
    } catch (e: any) {
      if (get().agentsGeneration !== gen) return;
      // A wall that cannot be read now must not keep showing what it showed
      // before: access revoked (403), the agent gone (404), or anything
      // else — the cached steering and ledger go with the error.
      set((s) => {
        const { [id]: _gone, ...rest } = s.agentWalls;
        return { agentWalls: rest, agentWallError: agentErrorMessage(e, 'Could not load the wall') };
      });
    }
  },

  subscribeAgent: async (id, on) => {
    await guarded(() => (on ? apiSubscribe(id) : apiUnsubscribe(id)), (res) => set((s) => {
      const wall = s.agentWalls[id];
      if (!wall) return {};
      return { agentWalls: { ...s.agentWalls, [id]: { ...wall, subscribed: res.subscribed, subscribers: res.subscribers } } };
    }));
  },

  steerAgent: async (id, text) => {
    const threadId = get().currentThreadId;
    await guarded(() => apiSteer(id, { text, op_id: mintOpId(), ...(threadId ? { thread_id: threadId } : {}) }), (res) => set((s) => {
      const wall = s.agentWalls[id];
      if (!wall) return {};
      return { agentWalls: { ...s.agentWalls, [id]: { ...wall, steering: [res.steering, ...wall.steering], activity: [res.activity, ...wall.activity] } } };
    }));
  },

  fetchGroups: async () => {
    const gen = get().agentsGeneration;
    try {
      const rows = await apiListGroups();
      if (get().agentsGeneration !== gen) return;
      set({ groups: rows, groupsError: null });
    } catch (e: any) {
      if (get().agentsGeneration !== gen) return;
      set({ groupsError: agentErrorMessage(e, 'Could not load groups') });
    }
  },

  createGroup: async (body) => {
    // a creation that lands after an identity/org switch belongs to the workspace it was asked in: never shown here
    const gen = get().agentsGeneration;
    const row = await apiCreateGroup(body);
    if (get().agentsGeneration !== gen) throw new Error('The workspace changed while the group was being created');
    set({ groups: [...get().groups.filter((g) => g.id !== row.id), row].sort((a, b) => a.name.localeCompare(b.name)) });
    return row;
  },

  disbandGroup: async (id) => {
    const gen = get().agentsGeneration;
    await apiDisbandGroup(id);
    if (get().agentsGeneration !== gen) return;
    set({ groups: get().groups.filter((g) => g.id !== id) });
  },

  openGroupChat: (groupId, groupName) => {
    const threads = get().savedThreads;
    const open = threads.find((t) => t.kind === 'group' && t.sourceId === groupId && !t.closedAt);
    if (open) {
      get().loadThread(open.id);
      return open.id;
    }
    const id = generateId();
    const now = new Date().toISOString();
    const thread: ConversationThread = {
      id, title: `@${groupName}`, preview: '', messages: [], createdAt: now, updatedAt: now, spaceId: null,
      detectedPerspective: null, detectedDomain: null, landingAgentTurns: [], perspectiveId: null, domainId: null,
      kind: 'group', starred: false, closedAt: null, lastOpenedAt: now, tabOrder: 0, sourceKind: 'group', sourceId: groupId, doc: null,
    };
    const creation = get().createAgentChatThread(thread);
    creations.set(id, creation);
    creation.then(() => { creations.delete(id); }, (err) => {
      creations.delete(id);
      set({ groupsError: agentErrorMessage(err, 'Could not open the group — try again') });
    });
    get().clearLandingChat();
    set({ currentView: 'landing', currentThreadId: id });
    return id;
  },

  deliverGroupTurn: async (threadId, groupId, turn) => {
    const gen = get().agentsGeneration;
    // where this belongs, captured now: a reply that lands after an identity/org switch is parked for the
    // workspace it was asked in (like an agent chat's, #202), never shown or stored in the one active by then
    const ns = getPersistenceNamespace();
    const thread = get().savedThreads.find((t) => t.id === threadId);
    const park = (turns: GroupChatTurn[]) => parkAgentChatSnapshot({
      threadId, kind: 'group', title: thread?.title || '', source: { kind: 'group', id: groupId }, turns, at: new Date().toISOString(),
    }, ns);
    const before = (get().savedThreads.find((t) => t.id === threadId)?.landingAgentTurns as GroupChatTurn[] | undefined) || [];
    const sent: GroupChatTurn = { ...turn, failed: false };
    const withUser = before.some((t) => t.id === turn.id) ? before.map((t) => (t.id === turn.id ? sent : t)) : [...before, sent];
    get().persistAgentChat(threadId, withUser);
    try {
      const creation = creations.get(threadId);
      if (creation && (!await creation || get().agentsGeneration !== gen)) throw new Error('The workspace changed');
      const out = await apiGroupChat(groupId, { text: turn.text, thread_id: threadId, op_id: turn.op_id });
      // a member's composed reply is kept ONLY as the composed reply (its words are redacted at rest by reference,
      // like an agent chat turn's); the roll-up's lines are status facts
      const replyTurn: GroupChatTurn = out.kind === 'answer'
        ? { id: turn.op_id ? `${turn.op_id}_reply` : generateId(), role: 'group', text: '', ts: new Date().toISOString(),
            spoken_as: out.spoken_as, reply: out.reply, session_id: out.session_id, promise: out.promise ?? null }
        : { id: turn.op_id ? `${turn.op_id}_reply` : generateId(), role: 'group', text: '', ts: new Date().toISOString(),
            spoken_as: null, lines: out.lines, session_id: out.session_id };
      const latestNow = (get().savedThreads.find((t) => t.id === threadId)?.landingAgentTurns as GroupChatTurn[] | undefined) || withUser;
      if (get().agentsGeneration !== gen) { park([...withUser.filter((t) => t.id !== replyTurn.id), replyTurn]); return; }
      get().persistAgentChat(threadId, [...latestNow.filter((t) => t.id !== replyTurn.id), replyTurn]);
    } catch (e) {
      const failed = withUser.map((t) => (t.id === turn.id ? { ...t, failed: true } : t));
      if (get().agentsGeneration !== gen) { park(failed); return; }
      const latest = (get().savedThreads.find((t) => t.id === threadId)?.landingAgentTurns as GroupChatTurn[] | undefined) || withUser;
      get().persistAgentChat(threadId, latest.map((t) => (t.id === turn.id ? { ...t, failed: true } : t)));
      throw e;
    }
  },

  openAgentChat: (agentId, agentName) => {
    const threads = get().savedThreads;
    const open = threads.find((t) => t.kind === 'agent_chat' && t.sourceId === agentId && !t.closedAt);
    if (open) {
      get().loadThread(open.id);
      return open.id;
    }
    const id = generateId();
    const now = new Date().toISOString();
    const title = `Chat · ${agentName}`;
    const thread: ConversationThread = {
      id, title, preview: '', messages: [], createdAt: now, updatedAt: now, spaceId: null,
      detectedPerspective: null, detectedDomain: null, landingAgentTurns: [], perspectiveId: null, domainId: null,
      kind: 'agent_chat', starred: false, closedAt: null, lastOpenedAt: now, tabOrder: 0, sourceKind: 'agent', sourceId: agentId, doc: null,
    };
    // The tab is the chat: the thread exists on the server from the moment
    // it is a tab, so the dedupe holds across a reload. Creation goes on
    // the thread's own write queue, so the first turn's persist waits
    // behind it — and no touch is sent: a thread just made needs none, and
    // one sent before the creation landed would 404.
    const creation = get().createAgentChatThread(thread);
    creations.set(id, creation);
    creation.then(() => { creations.delete(id); }, (err) => {
      creations.delete(id);
      set({ agentWallError: agentErrorMessage(err, 'Could not open the chat — try again') });
    });
    // On screen now, the way `loadThread` would show it: the conversation
    // on screen is parked first.
    get().clearLandingChat();
    set({ currentView: 'landing', currentThreadId: id });
    return id;
  },

  openChatTurn: (threadId, agentId) => {
    const inFlight = openings.get(threadId);
    if (inFlight) return inFlight;
    if (chatTurns(threadId).length > 0) return Promise.resolve();
    const gen = get().agentsGeneration;
    const p = apiOpening(agentId)
      .then(({ reply }) => {
        if (get().agentsGeneration !== gen) return;
        // Whatever arrived first takes the opening slot.
        if (chatTurns(threadId).length > 0) return;
        get().persistAgentChat(threadId, [{ id: 'opening', role: 'agent', ...reply, ts: new Date().toISOString() }]);
      })
      .finally(() => { openings.delete(threadId); });
    openings.set(threadId, p);
    return p;
  },

  deliverChatTurn: async (threadId, agentId, turn) => {
    const gen = get().agentsGeneration;
    // Where this belongs, captured now: a result that lands after an
    // identity/org switch is parked for the workspace it was asked in, never
    // shown or stored in the one that is active by then.
    const ns = getPersistenceNamespace();
    const thread = get().savedThreads.find((t) => t.id === threadId);
    const park = (turns: AgentChatTurn[]) => parkAgentChatSnapshot({
      threadId, kind: 'agent_chat', title: thread?.title || '', source: { kind: thread?.sourceKind ?? 'agent', id: thread?.sourceId ?? agentId },
      turns, at: new Date().toISOString(),
    }, ns);
    const before = chatTurns(threadId);
    const sent: AgentChatTurn = { ...turn, failed: false };
    const withUser = before.some((t) => t.id === turn.id) ? before.map((t) => (t.id === turn.id ? sent : t)) : [...before, sent];
    get().persistAgentChat(threadId, withUser);
    try {
      // A Home mention can send in the same tick that the chat tab is created.
      const creation = creations.get(threadId);
      if (creation) {
        if (!await creation || get().agentsGeneration !== gen) throw new Error('The chat workspace changed');
      }
      const { reply } = await apiChat(agentId, { text: turn.text, thread_id: threadId, op_id: turn.op_id });
      // Ids are stable across windows and retries — the server unions turns by
      // id on upsert, so the same reply written twice is one reply.
      const agentTurn: AgentChatTurn = { id: turn.op_id ? `${turn.op_id}_reply` : generateId(), role: 'agent', ...reply, ts: new Date().toISOString() };
      if (get().agentsGeneration !== gen) {
        // The steering is committed server-side; the reply must not be lost
        // with the switch. The old workspace's next fetch sends it.
        park([...withUser, agentTurn]);
        return;
      }
      get().persistAgentChat(threadId, [...chatTurns(threadId).map((t) => (t.id === turn.id ? { ...t, failed: false } : t)), agentTurn]);
    } catch (e) {
      if (get().agentsGeneration !== gen) {
        // Undelivered, and the workspace has changed: parked marked failed,
        // so the retry is offered there.
        park(withUser.map((t) => (t.id === turn.id ? { ...t, failed: true } : t)));
      } else {
        get().persistAgentChat(threadId, chatTurns(threadId).map((t) => (t.id === turn.id ? { ...t, failed: true } : t)));
      }
      throw e;
    }
  },
  };
};
